/**
 * 個別設定 (override) の報酬表ビューア モーダル (v3.2.29+ / v3.3.0 で加算方式に反転)。
 *
 * CommissionOverridesSection の各行から起動。
 * override.items[] を表示し、SA 受取人ならベース/開拓顧客/並列モード切替 + PDF 出力。
 * v3.3.0+: PDF 出力カラムの選択チップを追加。
 *
 * 開拓顧客加算率は「個別設定の商品ごとの率 → 個別設定の既定 → セットのカテゴリー別加算率」の順に
 * 決める (2026-11-02+、resolveEffectiveSelfDevelopedAdditionRate)。カテゴリー別の率は items の
 * productId → categoryId を商品マスタ + ProductCategory から逆引きして引く
 * (旧「弊社紹介減額率」データは加算率として読み替え)。
 */
import { resolveEffectiveSelfDevelopedAdditionRate } from '@goenhub/shared';
import { FileDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import { buildProductIdToCategoryId } from './categoryCatalogLink';
import {
  buildPrintableHtml,
  CommissionTableBody,
  computeRowsCalc,
  defaultPdfColumnSelection,
  openPrintWindow,
  PdfColumnPicker,
  pdfColumnsForMode,
  REFERRAL_MODE_LABEL,
  type PdfColumnSelection,
  type ReferralViewMode,
} from './CommissionTableView';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useCommissionOverrides } from '@/hooks/useCommissionOverrides';
import { useCommissionSetDetail } from '@/hooks/useCommissions';
import { useCatalogItemsForCommissionSet } from '@/hooks/useProductCatalogs';
import { useProductCategories } from '@/hooks/useProductCategories';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  overrideId: string;
}

export function CommissionOverrideTableViewerDialog({
  open,
  onOpenChange,
  setId,
  overrideId,
}: Props) {
  const overrides = useCommissionOverrides(open ? setId : null);
  const detail = useCommissionSetDetail(open ? setId : null);
  // v3.2.30+: 季節モードではなく、このセットに紐付くカタログの価格を参照する
  const allItems = useCatalogItemsForCommissionSet(open ? setId : null);
  const categories = useProductCategories();

  const override = useMemo(
    () => (overrides.data ?? []).find((o) => o.id === overrideId) ?? null,
    [overrides.data, overrideId],
  );

  const setName = detail.data?.set.name ?? '';
  const setForRates = detail.data?.set ?? null;

  // productId → categoryId (商品マスタとカテゴリーから逆引き)
  const productIdToCategoryId = useMemo(
    () => buildProductIdToCategoryId(allItems.data ?? [], categories.data ?? []),
    [allItems.data, categories.data],
  );

  const getAdditionRateForProduct = (productId: string): number =>
    resolveEffectiveSelfDevelopedAdditionRate({
      set: setForRates,
      categoryId: productIdToCategoryId.get(productId) ?? null,
      overrideRate: override?.selfDevelopedAdditionRate ?? null,
      itemRate:
        override?.items.find((it) => it.productId === productId)?.selfDevelopedAdditionRate ?? null,
    }).rate;

  // 開拓顧客加算率の設定の要約 (受取人情報の帯に出す)
  const additionRateSummary = useMemo(() => {
    if (!override || override.recipientType !== 'sa') return null;
    const base =
      typeof override.selfDevelopedAdditionRate === 'number'
        ? `個別 +${override.selfDevelopedAdditionRate}%`
        : 'セットの標準';
    const perItem = override.items.filter(
      (it) => it.value > 0 && typeof it.selfDevelopedAdditionRate === 'number',
    ).length;
    return perItem > 0 ? `${base} (商品ごとの指定 ${perItem} 件)` : base;
  }, [override]);

  const [referralViewMode, setReferralViewMode] = useState<ReferralViewMode>('normal');
  // v3.3.0+: PDF 出力カラム選択
  const [pdfColumns, setPdfColumns] = useState<PdfColumnSelection>(defaultPdfColumnSelection);

  const isSaRole = override?.recipientType === 'sa';

  // v3.2.29.5: 商品マスタにない productId (orphan) を除外
  const currentProductIds = useMemo(
    () => new Set((allItems.data ?? []).map((p) => p.id)),
    [allItems.data],
  );
  const rawItemsActive = useMemo(
    () => (override?.items ?? []).filter((it) => it.value > 0),
    [override],
  );
  const orphanCount = useMemo(
    () => rawItemsActive.filter((it) => !currentProductIds.has(it.productId)).length,
    [rawItemsActive, currentProductIds],
  );
  const itemsActive = useMemo(
    () => rawItemsActive.filter((it) => currentProductIds.has(it.productId)),
    [rawItemsActive, currentProductIds],
  );

  const rowsCalc = useMemo(() => {
    if (!override) return [];
    return computeRowsCalc({
      items: itemsActive,
      allItems: allItems.data ?? [],
      defaultType: override.defaultType,
      isSaRole,
      getAdditionRateForProduct,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override, itemsActive, allItems.data, isSaRole, productIdToCategoryId, setForRates]);

  const modeForRender: ReferralViewMode = isSaRole ? referralViewMode : 'normal';

  const handleExportPdf = () => {
    if (!override || rowsCalc.length === 0) return;
    const extraNotes: string[] = ['個別設定 (override) 適用版'];
    if (override.briefingFee && override.briefingFee > 0) {
      extraNotes.push(`個別説明代行料: ¥${override.briefingFee.toLocaleString()} / 件`);
    }
    if (additionRateSummary && modeForRender !== 'normal') {
      extraNotes.push(`開拓顧客加算率: ${additionRateSummary}`);
    }
    if (override.memo) extraNotes.push(`メモ: ${override.memo}`);

    const html = buildPrintableHtml({
      title: `報酬表 (個別設定) ${setName} / ${override.recipientName} (${REFERRAL_MODE_LABEL[modeForRender]})`,
      metaLines: [
        `セット: ${setName}`,
        `対象: ${override.recipientName} (${override.recipientType === 'sa' ? 'SA (営業パートナー)' : '社員 (営業内勤)'})`,
        `表示モード: ${REFERRAL_MODE_LABEL[modeForRender]}`,
      ],
      mode: modeForRender,
      rows: rowsCalc,
      extraNotes,
      columns: pdfColumnsForMode(modeForRender).filter((k) => pdfColumns[k]),
    });
    openPrintWindow(html);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            報酬表 (個別設定) — {setName}
            {override ? ` / ${override.recipientName}` : ''}
          </DialogTitle>
        </DialogHeader>

        {overrides.isLoading || detail.isLoading || allItems.isLoading ? (
          <div className="py-8 text-center">
            <Spinner />
          </div>
        ) : !override ? (
          <p className="py-6 text-sm text-[var(--text-muted)] text-center">
            個別設定が見つかりません。
          </p>
        ) : (
          <div className="space-y-4">
            {/* 受取人情報 */}
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-xs">
              <Badge variant="accent">
                {override.recipientType === 'sa' ? 'SA (営業パートナー)' : '社員 (営業内勤)'}
              </Badge>
              <span className="font-semibold">{override.recipientName}</span>
              <span className="text-[var(--text-muted)]">
                既定タイプ: {override.defaultType === 'rate' ? 'レート (%)' : '固定額 (円)'}
              </span>
              {override.briefingFee != null && override.briefingFee > 0 && (
                <span className="text-[var(--text-muted)]">
                  個別説明代行料: ¥{override.briefingFee.toLocaleString()} / 件
                </span>
              )}
              {additionRateSummary && (
                <span className="text-[var(--text-muted)]">
                  開拓顧客加算率: {additionRateSummary}
                </span>
              )}
            </div>

            {/* v3.2.29.5: orphan 件数の通知 */}
            {orphanCount > 0 && (
              <div className="rounded-md border border-amber-300/40 bg-amber-50/40 dark:bg-amber-950/15 p-2 text-[11px]">
                <span className="font-semibold text-amber-700">
                  ⚠️ {orphanCount} 件の旧データを表示から除外しました
                </span>
                <span className="text-[10px] text-[var(--text-muted)] ml-2">
                  (過去のカタログ差替え時の残骸 — 報酬計算には影響しません)
                </span>
              </div>
            )}

            {/* SA 受取人のときのみモード切替 + 全タブで PDF 出力 */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {isSaRole ? (
                <div className="flex items-center gap-2 rounded-md border border-emerald-300/40 bg-emerald-50/30 dark:bg-emerald-950/10 p-2">
                  <span className="text-[11px] font-medium text-[var(--text-secondary)]">
                    表示モード:
                  </span>
                  {(
                    [
                      { value: 'normal', label: 'ベース' },
                      { value: 'selfDeveloped', label: '開拓顧客' },
                      { value: 'parallel', label: '並列' },
                    ] as const
                  ).map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setReferralViewMode(m.value)}
                      className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                        referralViewMode === m.value
                          ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                          : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                  <span className="text-[10px] text-[var(--text-muted)] ml-1">
                    加算率はこの個別設定の値 (未指定の商品はセットのカテゴリー別「開拓顧客加算率」)
                    を反映
                  </span>
                </div>
              ) : (
                <div className="text-[11px] text-[var(--text-muted)]">
                  社員報酬は開拓顧客加算の対象外
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleExportPdf}
                disabled={rowsCalc.length === 0}
                title="現在の表示モード・選択カラムを PDF として出力"
              >
                <FileDown className="size-3.5" />
                PDF 出力
              </Button>
            </div>

            {/* v3.3.0+: PDF 出力カラム選択 */}
            <PdfColumnPicker mode={modeForRender} selection={pdfColumns} onChange={setPdfColumns} />

            {rowsCalc.length === 0 ? (
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-6 text-center text-xs text-[var(--text-muted)] italic">
                この個別設定には有効な商品報酬 (value &gt; 0) がありません。
              </div>
            ) : (
              <CommissionTableBody rows={rowsCalc} mode={modeForRender} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
